export interface HoverPreviewAnchor {
  left: number;
  top: number;
  bottom: number;
}

export interface HoverPreviewViewport {
  width: number;
  height: number;
}

export interface HoverPreviewPosition {
  left: number;
  top: number;
  direction: 'down' | 'up';
}

export interface HoverPreviewSize {
  width: number;
  height: number;
}

export interface SideHoverPreviewAnchor extends HoverPreviewAnchor {
  right: number;
}

export interface SideHoverPreviewPosition {
  left: number;
  top: number;
  side: 'left' | 'right';
}

export const BATCH_HOVER_GALLERY_WIDTH = 422;
export const BATCH_HOVER_GALLERY_HEIGHT = 390;
const VIEWPORT_GUTTER = 8;
const ANCHOR_GAP = 12;

/** Position a fixed gallery in the viewport, away from overflow-clipping list ancestors. */
export function positionVerticalHoverPreview(
  anchor: HoverPreviewAnchor,
  viewport: HoverPreviewViewport,
  size: HoverPreviewSize,
): HoverPreviewPosition {
  const maxLeft = Math.max(VIEWPORT_GUTTER, viewport.width - size.width - VIEWPORT_GUTTER);
  const left = Math.min(Math.max(VIEWPORT_GUTTER, anchor.left), maxLeft);
  const spaceBelow = viewport.height - anchor.bottom;
  const spaceAbove = anchor.top;
  const direction =
    spaceBelow >= size.height + VIEWPORT_GUTTER || spaceAbove < size.height + VIEWPORT_GUTTER
      ? 'down'
      : 'up';
  const desiredTop =
    direction === 'down'
      ? anchor.bottom + VIEWPORT_GUTTER
      : anchor.top - size.height - VIEWPORT_GUTTER;
  const maxTop = Math.max(VIEWPORT_GUTTER, viewport.height - size.height - VIEWPORT_GUTTER);

  return { left, top: Math.min(Math.max(VIEWPORT_GUTTER, desiredTop), maxTop), direction };
}

/** Position a fixed preview beside its trigger, preferring the trigger's left side. */
export function positionSideHoverPreview(
  anchor: SideHoverPreviewAnchor,
  viewport: HoverPreviewViewport,
  size: HoverPreviewSize,
): SideHoverPreviewPosition {
  const fitsLeft = anchor.left >= size.width + ANCHOR_GAP + VIEWPORT_GUTTER;
  const desiredLeft = fitsLeft ? anchor.left - size.width - ANCHOR_GAP : anchor.right + ANCHOR_GAP;
  const maxLeft = Math.max(VIEWPORT_GUTTER, viewport.width - size.width - VIEWPORT_GUTTER);
  const maxTop = Math.max(VIEWPORT_GUTTER, viewport.height - size.height - VIEWPORT_GUTTER);

  return {
    left: Math.min(Math.max(VIEWPORT_GUTTER, desiredLeft), maxLeft),
    top: Math.min(Math.max(VIEWPORT_GUTTER, anchor.top), maxTop),
    side: fitsLeft ? 'left' : 'right',
  };
}

export function positionBatchHoverPreview(
  anchor: HoverPreviewAnchor,
  viewport: HoverPreviewViewport,
): HoverPreviewPosition {
  return positionVerticalHoverPreview(anchor, viewport, {
    width: BATCH_HOVER_GALLERY_WIDTH,
    height: BATCH_HOVER_GALLERY_HEIGHT,
  });
}
